if (!customElements.get('localization-switcher')) {
  const EASE = 'cubic-bezier(0.2, 0.8, 0.2, 1)';
  const GAP = 8;
  const EDGE = 8;
  // A leading locale or market segment: /en, /fr, /en-in, /fr-eu… No storefront route
  // starts with a two-letter segment, so this never eats a real path.
  const LOCALE_PREFIX = /^\/[a-z]{2}(?:-[a-z0-9]{2,8})?(?=\/|$)/i;

  customElements.define(
    'localization-switcher',
    class LocalizationSwitcher extends HTMLElement {
      connectedCallback() {
        this.form = this.querySelector('[data-ls-form]');
        this.countryInput = this.querySelector('[data-ls-country-input]');
        this.languageInput = this.querySelector('[data-ls-language-input]');
        this.returnInput = this.querySelector('[data-ls-return-input]');

        if (!this.form || !this.countryInput || !this.returnInput) return;

        this.preferenceKey = this.dataset.preferenceKey || 'ml_market_pref_v4';
        this.consentKey = this.dataset.consentKey || 'ml_cookie_gate_v4';
        this.supportsPopover = typeof this.querySelector('[data-ls-panel]')?.showPopover === 'function';
        this.teardown = [];
        this.openField = null;
        this.motion = new WeakMap();

        this.fields = Array.from(this.querySelectorAll('[data-ls-field]')).map((field) => this.bindField(field));

        this.bindGlobal();
        this.syncLanguageUI();
        this.syncPreference();
      }

      disconnectedCallback() {
        this.teardown.forEach((off) => off());
        this.teardown = [];

        (this.fields || []).forEach((entry) => entry.options.forEach((option) => this.settle(option)));
      }

      /* ---------- current state ---------- */

      currentCountry() {
        return (this.dataset.currentCountry || '').toUpperCase();
      }

      languageRoots() {
        return Array.from(this.querySelectorAll('[data-ls-option-root]')).map((option) => option.dataset.lsOptionRoot);
      }

      knownRoots() {
        return this.languageRoots().concat([this.dataset.currentRoot || '/']);
      }

      trim(root) {
        return !root || root === '/' ? '' : root.replace(/\/+$/, '');
      }

      matchesRoot(path, root) {
        const target = this.trim(root);
        if (!target) return false;

        const rest = path.slice(target.length);
        return path.startsWith(target) && (rest === '' || rest.charAt(0) === '/');
      }

      // Strips every leading prefix, not just the current market's own roots. Stripping only
      // those left foreign ones in place — Weglot's /en, another market's /fr-in — and the new
      // root was stacked on top of them: /en/fr-in/en/en-in. This also cleans such URLs up.
      strippedPath() {
        const roots = this.knownRoots()
          .map((root) => this.trim(root))
          .filter(Boolean);
        let path = window.location.pathname;
        let previous;

        do {
          previous = path;

          const matched = roots
            .filter((root) => this.matchesRoot(path, root))
            .sort((a, b) => b.length - a.length)[0];

          path = matched ? path.slice(matched.length) : path.replace(LOCALE_PREFIX, '');
        } while (path !== previous);

        return path;
      }

      currentLanguage() {
        const path = window.location.pathname;

        const active = Array.from(this.querySelectorAll('[data-ls-option-root]'))
          .filter((option) => this.matchesRoot(path, option.dataset.lsOptionRoot))
          .sort((a, b) => this.trim(b.dataset.lsOptionRoot).length - this.trim(a.dataset.lsOptionRoot).length)[0];

        return active ? active.dataset.lsOption : this.dataset.currentLanguage || '';
      }

      languageRootFor(code) {
        const option = this.querySelector(`[data-ls-option-root][data-ls-option="${CSS.escape(code)}"]`);
        return option ? option.dataset.lsOptionRoot : '/';
      }

      /* ---------- dropdown ---------- */

      bindField(field) {
        const toggle = field.querySelector('[data-ls-toggle]');
        const panel = field.querySelector('[data-ls-panel]');
        const filter = field.querySelector('[data-ls-filter]');
        const list = field.querySelector('[data-ls-list]');
        const options = Array.from(field.querySelectorAll('[data-ls-option]'));
        const entry = { field, toggle, panel, filter, list, options, placement: null, naturalHeight: 0 };

        const onToggleClick = () => (this.isOpen(entry) ? this.close(entry) : this.open(entry));

        const onPanelClick = (event) => {
          const option = event.target.closest('[data-ls-option]');
          if (!option) return;

          this.close(entry);
          this.select(field.dataset.lsField, option.dataset.lsOption);
        };

        const onKeydown = (event) => {
          if (event.key === 'Escape' && this.isOpen(entry)) {
            this.close(entry);
            toggle.focus();
            return;
          }

          if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            event.preventDefault();
            if (!this.isOpen(entry)) this.open(entry);
            this.moveFocus(entry, event.key === 'ArrowDown' ? 1 : -1);
          }
        };

        const onFilter = () => this.applyFilter(entry);

        // popover=auto light-dismisses on outside click and Escape without telling us,
        // so mirror that back into aria-expanded and openField.
        const onPanelToggle = (event) => {
          if (event.newState !== 'closed') return;

          toggle.setAttribute('aria-expanded', 'false');
          if (this.openField === entry) this.openField = null;
        };

        toggle.addEventListener('click', onToggleClick);
        panel.addEventListener('click', onPanelClick);
        panel.addEventListener('toggle', onPanelToggle);
        field.addEventListener('keydown', onKeydown);
        if (filter) filter.addEventListener('input', onFilter);

        this.teardown.push(() => {
          toggle.removeEventListener('click', onToggleClick);
          panel.removeEventListener('click', onPanelClick);
          panel.removeEventListener('toggle', onPanelToggle);
          field.removeEventListener('keydown', onKeydown);
          if (filter) filter.removeEventListener('input', onFilter);
        });

        return entry;
      }

      bindGlobal() {
        const onResize = () => {
          if (this.openField) this.position(this.openField, true);
        };

        const onScroll = (event) => {
          if (!this.openField) return;
          if (this.openField.panel.contains(event.target)) return;

          this.position(this.openField);
        };

        const onDocumentClick = (event) => {
          if (this.supportsPopover) return;
          if (this.openField && !this.openField.field.contains(event.target)) this.close(this.openField);
        };

        window.addEventListener('resize', onResize);
        window.addEventListener('scroll', onScroll, true);
        document.addEventListener('click', onDocumentClick);

        this.teardown.push(() => {
          window.removeEventListener('resize', onResize);
          window.removeEventListener('scroll', onScroll, true);
          document.removeEventListener('click', onDocumentClick);
        });
      }

      isOpen(entry) {
        return this.openField === entry;
      }

      open(entry) {
        if (this.openField && this.openField !== entry) this.close(this.openField);

        if (entry.filter) {
          entry.filter.value = '';
          this.applyFilter(entry, true);
        }

        this.resolvePlacement(entry);

        if (this.supportsPopover) {
          entry.panel.showPopover();
        } else {
          entry.panel.classList.add('is-open');
        }

        entry.toggle.setAttribute('aria-expanded', 'true');
        this.openField = entry;
        this.position(entry, true);
        this.measure(entry);

        if (entry.filter) entry.filter.focus();

        const selected = entry.panel.querySelector('[aria-selected="true"]');
        if (selected) selected.scrollIntoView({ block: 'nearest' });
      }

      close(entry) {
        if (this.supportsPopover && entry.panel.matches(':popover-open')) {
          entry.panel.hidePopover();
        } else {
          entry.panel.classList.remove('is-open');
        }

        entry.toggle.setAttribute('aria-expanded', 'false');
        if (this.openField === entry) this.openField = null;
      }

      /* ---------- placement ---------- */

      cap() {
        return Math.min(window.innerWidth >= 750 ? 380 : 320, window.innerHeight * 0.6);
      }

      measure(entry) {
        if (!entry.list) return;

        entry.naturalHeight = (entry.filter ? entry.filter.offsetHeight : 0) + entry.list.scrollHeight;
      }

      // Above by default — this lives in the footer — and below only when the panel
      // genuinely cannot fit above and there is more room the other way.
      resolvePlacement(entry) {
        const anchor = entry.toggle.getBoundingClientRect();
        const above = anchor.top - GAP - EDGE;
        const below = window.innerHeight - anchor.bottom - GAP - EDGE;
        const desired = Math.min(entry.naturalHeight || this.cap(), this.cap());

        entry.placement = above >= desired || above >= below ? 'top' : 'bottom';
        entry.panel.dataset.lsPlacement = entry.placement;
      }

      // The panel is pinned by the edge that faces the toggle — `bottom` when it opens
      // upwards — so filtering it down to one row shrinks it away from the toggle instead
      // of detaching from it. Width is frozen on open for the same reason: a max-content
      // panel would otherwise resize sideways on every keystroke.
      position(entry, recalc) {
        if (!this.supportsPopover) return;

        const panel = entry.panel;
        const anchor = entry.toggle.getBoundingClientRect();

        if (recalc || !entry.placement) this.resolvePlacement(entry);

        if (recalc) {
          panel.style.width = '';
          panel.style.minWidth = `${anchor.width}px`;
          panel.style.width = `${panel.offsetWidth}px`;
        }

        const room =
          entry.placement === 'top'
            ? anchor.top - GAP - EDGE
            : window.innerHeight - anchor.bottom - GAP - EDGE;

        panel.style.maxHeight = `${Math.max(140, Math.min(this.cap(), room))}px`;

        if (entry.placement === 'top') {
          panel.style.top = 'auto';
          panel.style.bottom = `${Math.max(EDGE, window.innerHeight - anchor.top + GAP)}px`;
        } else {
          panel.style.bottom = 'auto';
          panel.style.top = `${Math.max(EDGE, anchor.bottom + GAP)}px`;
        }

        const left = Math.min(Math.max(EDGE, anchor.left), Math.max(EDGE, window.innerWidth - panel.offsetWidth - EDGE));
        panel.style.left = `${left}px`;
      }

      moveFocus(entry, direction) {
        const options = entry.options.filter((option) => !option.hidden && !option.classList.contains('is-leaving'));
        if (!options.length) return;

        const active = options.indexOf(document.activeElement);
        const start = active === -1 ? options.findIndex((o) => o.getAttribute('aria-selected') === 'true') : active;
        const next = Math.min(Math.max(start + direction, 0), options.length - 1);

        options[next].focus();
      }

      /* ---------- filtering ---------- */

      reduceMotion() {
        return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      }

      play(element, keyframes, timing) {
        this.stop(element);

        const animation = element.animate(keyframes, Object.assign({ easing: EASE }, timing));
        this.motion.set(element, animation);

        return animation;
      }

      stop(element) {
        const animation = this.motion.get(element);
        if (!animation) return;

        animation.cancel();
        this.motion.delete(element);
      }

      settle(option) {
        this.stop(option);

        if (!option.classList.contains('is-leaving')) return;

        option.classList.remove('is-leaving');
        option.style.top = '';
      }

      optionMatches(option, term) {
        return !term || option.textContent.toLowerCase().indexOf(term) !== -1;
      }

      applyFilter(entry, immediate) {
        if (!entry.filter) return;

        const term = entry.filter.value.trim().toLowerCase();

        if (immediate || !entry.list || !this.isOpen(entry) || this.reduceMotion()) {
          entry.options.forEach((option) => {
            this.settle(option);
            option.hidden = !this.optionMatches(option, term);
          });

          this.syncGroups(entry);
          return;
        }

        const listTop = entry.list.getBoundingClientRect().top;
        const scrollTop = entry.list.scrollTop;
        const panelHeight = entry.panel.offsetHeight;
        const before = new Map();

        entry.options.forEach((option) => {
          if (option.hidden || option.classList.contains('is-leaving')) return;
          before.set(option, option.getBoundingClientRect().top);
        });

        const leaving = [];

        entry.options.forEach((option) => {
          if (this.optionMatches(option, term)) {
            this.settle(option);
            option.hidden = false;
            return;
          }

          if (!before.has(option)) {
            this.settle(option);
            option.hidden = true;
            return;
          }

          leaving.push([option, before.get(option) - listTop + scrollTop]);
        });

        // Out of flow immediately, so the rows below close the gap while these fade.
        leaving.forEach(([option, offset]) => this.leave(option, offset));

        this.syncGroups(entry);
        this.position(entry);
        this.shiftInto(entry, before, panelHeight);
      }

      leave(option, offset) {
        option.classList.add('is-leaving');
        option.style.top = `${offset}px`;

        const animation = this.play(
          option,
          [
            { opacity: 1, transform: 'translateY(0)' },
            { opacity: 0, transform: 'translateY(-4px)' },
          ],
          { duration: 150, fill: 'forwards' }
        );

        animation.finished
          .then(() => {
            if (this.motion.get(option) !== animation) return;

            this.settle(option);
            option.hidden = true;
          })
          .catch(() => {});
      }

      shiftInto(entry, before, panelHeight) {
        // A height animation still in flight would report its own interpolated value
        // instead of the layout we are measuring towards, so drop it before reading.
        this.stop(entry.panel);

        const bounds = entry.list.getBoundingClientRect();
        const height = entry.panel.offsetHeight;

        if (Math.abs(height - panelHeight) > 0.5) {
          this.play(entry.panel, [{ height: `${panelHeight}px` }, { height: `${height}px` }], { duration: 260 });
        }

        entry.options.forEach((option) => {
          if (option.hidden || option.classList.contains('is-leaving')) return;

          const rect = option.getBoundingClientRect();
          if (rect.bottom < bounds.top - 40 || rect.top > bounds.bottom + 40) {
            this.stop(option);
            return;
          }

          const previous = before.get(option);

          if (previous === undefined) {
            this.play(option, [{ opacity: 0, transform: 'translateY(-6px)' }, { opacity: 1, transform: 'none' }], {
              duration: 180,
            });
            return;
          }

          const delta = previous - rect.top;
          if (Math.abs(delta) < 0.5) return;

          this.play(option, [{ transform: `translateY(${delta}px)` }, { transform: 'translateY(0)' }], {
            duration: 260,
          });
        });
      }

      syncGroups(entry) {
        entry.panel.querySelectorAll('[data-ls-group]').forEach((group) => {
          let sibling = group.nextElementSibling;
          let visible = false;

          while (sibling && !sibling.hasAttribute('data-ls-group')) {
            if (!sibling.hidden) visible = true;
            sibling = sibling.nextElementSibling;
          }

          group.hidden = !visible;
        });
      }

      /* ---------- applying a choice ---------- */

      select(kind, value) {
        const country = kind === 'country' ? value : this.currentCountry();
        const language = kind === 'language' ? value : this.currentLanguage();

        if (country === this.currentCountry() && language === this.currentLanguage()) return;

        this.writePreference(country, language);
        this.submit(country, language);
      }

      submit(country, language) {
        // The language roots on this page belong to the current market (France's English is
        // /en, International's is /en-in). On a country change send the bare path instead and
        // let Shopify prefix it for the new market, as it already does from the French root.
        const sameCountry = country === this.currentCountry();
        const root = sameCountry ? this.trim(this.languageRootFor(language)) : '';
        let path = this.strippedPath();

        if (path === '/') path = '';

        // France + French lives on the bare root. The country does not change, so there is
        // nothing to tell Shopify — only the /en has to go. Go straight there: the
        // /localization round trip is what kept landing back on /en.
        if (sameCountry && !root) {
          window.location.assign((path || '/') + window.location.search);
          return;
        }

        this.countryInput.value = country;
        if (this.languageInput) this.languageInput.value = language;
        this.returnInput.value = (root + path || '/') + window.location.search;

        // The unprefixed endpoint, as on every other market. On France's English pages the
        // action arrives rewritten to /en/localization, which sends the visitor back to /en.
        this.form.action = `${window.location.origin}/localization`;
        this.form.submit();
      }

      // Liquid cannot see the /en prefix — Weglot serves that URL and request.path comes back
      // without it — so the server renders the wrong language as selected. window.location can
      // see it, so correct the label and the selected state once the component is alive.
      syncLanguageUI() {
        const language = this.currentLanguage();
        if (!language || language === this.dataset.currentLanguage) return;

        const options = Array.from(this.querySelectorAll('[data-ls-option-root]'));
        const active = options.find((option) => option.dataset.lsOption === language);
        if (!active) return;

        options.forEach((option) => {
          option.setAttribute('aria-selected', option === active ? 'true' : 'false');
        });

        const label = this.querySelector('[data-ls-language-label]');
        if (label) label.textContent = active.textContent.trim();

        this.dataset.currentLanguage = language;
      }

      /* ---------- persistence ---------- */

      readPreference() {
        try {
          return JSON.parse(window.localStorage.getItem(this.preferenceKey) || 'null');
        } catch (error) {
          return null;
        }
      }

      canPersist() {
        // The cookie banner shows while `preferenceKey` is absent, and that banner is also
        // where tracking consent is asked. Writing the key before the banner has been answered
        // would silently suppress the consent request, so hold off until it has been.
        try {
          return !!window.localStorage.getItem(this.consentKey) || !!this.readPreference();
        } catch (error) {
          return false;
        }
      }

      writePreference(country, language) {
        if (!this.canPersist()) return;

        const stored = this.readPreference() || {};
        const option = this.querySelector(`[data-ls-option="${CSS.escape(country)}"][data-ls-currency]`);

        this.persist({
          country: country,
          currency: option ? option.dataset.lsCurrency : stored.currency || '',
          language: language,
          root: null,
        });
      }

      syncPreference() {
        const stored = this.readPreference();
        if (!stored) return;
        if (!this.canPersist()) return;

        const option = this.querySelector(`[data-ls-option="${CSS.escape(this.currentCountry())}"][data-ls-currency]`);

        this.persist({
          country: this.currentCountry(),
          currency: option ? option.dataset.lsCurrency : stored.currency || '',
          language: this.currentLanguage(),
          root: this.trim(this.dataset.currentRoot) || '/',
        });
      }

      persist(value) {
        try {
          window.localStorage.setItem(this.preferenceKey, JSON.stringify(value));
        } catch (error) {
          /* private mode — the choice still applies to this page load */
        }
      }
    }
  );
}
